import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StorageService } from '../storage.service';

// Base URL para API (mesmo padrão do ApiService)
const getBaseUrl = (): string => {
    return (environment as any).apiUrl || '/api';
};

export interface OfflineAction {
    id: string;
    type: 'POST' | 'PUT' | 'DELETE';
    url: string;
    data?: any;
    timestamp: number;
    retryCount: number;
    maxRetries: number;
}

export interface CacheItem {
    key: string;
    data: any;
    timestamp: number;
    ttl: number; // Time to live em milissegundos
    priority: 'high' | 'medium' | 'low';
}

/**
 * Serviço para gerenciar funcionalidades offline
 * - Cache de dados
 * - Fila de sincronização
 * - Detecção de conectividade
 */
@Injectable({
    providedIn: 'root',
})
export class OfflineService {
    private readonly http = inject(HttpClient);
    private readonly storageService = inject(StorageService);

    // Signals para estado reativo
    private readonly _isOffline = signal<boolean>(false);
    private readonly _pendingActions = signal<OfflineAction[]>([]);
    private readonly _cacheItems = signal<Map<string, CacheItem>>(new Map());
    private readonly _syncProgress = signal<number>(0);
    private readonly _lastSync = signal<Date | null>(null);

    // Computed values para acesso externo
    readonly isOffline = computed(() => this._isOffline());
    readonly pendingActions = computed(() => this._pendingActions());
    readonly pendingActionsCount = computed(() => this._pendingActions().length);
    readonly syncProgress = computed(() => this._syncProgress());
    readonly lastSync = computed(() => this._lastSync());
    readonly cacheSize = computed(() => this._cacheItems().size);

    // Configurações
    private readonly maxCacheSize = 100;
    private readonly defaultTTL = 60 * 60 * 1000; // 1 hora
    private readonly syncQueueKey = 'offline_sync_queue';
    private readonly cacheKey = 'offline_cache';

    constructor() {
        this.initializeService();
    }

    /**
     * Inicializa o serviço
     */
    private initializeService(): void {
        // Carregar dados salvos
        this.carregarDadosSalvos();

        // Escutar mudanças de conectividade
        this.escutarMudancasConexao();

        // Effect para salvar dados automaticamente
        effect(() => {
            this.salvarDados();
        });

        // Verificar conectividade inicial
        this.verificarConectividade();
    }

    /**
     * Escuta mudanças na conectividade
     */
    private escutarMudancasConexao(): void {
        window.addEventListener('online', () => {
            this._isOffline.set(false);
            // Sincronização agora é gerenciada pelo SyncQueueService
            // Não sincronizar mais aqui para evitar duplicação
        });

        window.addEventListener('offline', () => {
            this._isOffline.set(true);
        });
    }

    /**
     * Verifica conectividade atual
     */
    private verificarConectividade(): void {
        this._isOffline.set(!navigator.onLine);
    }

    /**
     * Carrega dados salvos do storage
     */
    private carregarDadosSalvos(): void {
        try {
            // Carregar ações pendentes
            const savedActions = this.storageService.get(this.syncQueueKey);
            if (savedActions) {
                this._pendingActions.set(savedActions);
            }

            // Carregar cache
            const savedCache = this.storageService.get(this.cacheKey);
            if (savedCache) {
                const cacheMap = new Map<string, CacheItem>();
                Object.entries(savedCache).forEach(([key, item]: [string, any]) => {
                    cacheMap.set(key, item as CacheItem);
                });
                this._cacheItems.set(cacheMap);
            }
        } catch (error) {
            console.error('Erro ao carregar dados offline:', error);
        }
    }

    /**
     * Salva dados no storage
     */
    private salvarDados(): void {
        try {
            // Salvar ações pendentes
            this.storageService.set(this.syncQueueKey, this._pendingActions());

            // Salvar cache
            const cacheData: Record<string, CacheItem> = {};
            this._cacheItems().forEach((item, key) => {
                cacheData[key] = item;
            });
            this.storageService.set(this.cacheKey, cacheData);
        } catch (error) {
            console.error('Erro ao salvar dados offline:', error);
        }
    }

    /**
     * Adiciona uma ação à fila de sincronização
     * @deprecated Use SyncQueueService.adicionarItem() diretamente
     * Este método foi mantido apenas para compatibilidade, mas não faz nada
     * para evitar duplicação com SyncQueueService
     */
    adicionarAcaoOffline(action: Omit<OfflineAction, 'id' | 'timestamp' | 'retryCount'>): void {
        // Método desabilitado para evitar duplicação
        // A sincronização agora é gerenciada exclusivamente pelo SyncQueueService
    }

    /**
     * Sincroniza ações pendentes quando online
     */
    async sincronizarAcoesPendentes(): Promise<void> {
        if (this.isOffline() || this.pendingActionsCount() === 0) {
            return;
        }

        const actions = this._pendingActions();
        let processed = 0;

        for (const action of actions) {
            try {
                await this.executarAcao(action);
                processed++;
                this._syncProgress.set((processed / actions.length) * 100);
            } catch (error) {
                console.error('Erro ao sincronizar ação:', action, error);
                action.retryCount++;

                if (action.retryCount >= action.maxRetries) {
                    // Remover ação após máximo de tentativas
                    this.removerAcaoPendente(action.id);
                }
            }
        }

        this._lastSync.set(new Date());
        this._syncProgress.set(100);
    }

    /**
     * Executa uma ação HTTP
     */
    private async executarAcao(action: OfflineAction): Promise<void> {
        // Normalizar URL: garantir que tenha /api se necessário
        const url = this.normalizeUrl(action.url);

        switch (action.type) {
            case 'POST':
                await this.http.post(url, action.data).toPromise();
                break;
            case 'PUT':
                await this.http.put(url, action.data).toPromise();
                break;
            case 'DELETE':
                await this.http.delete(url).toPromise();
                break;
        }

        // Remover ação após execução bem-sucedida
        this.removerAcaoPendente(action.id);
    }

    /**
     * Remove uma ação pendente
     */
    private removerAcaoPendente(actionId: string): void {
        const currentActions = this._pendingActions();
        const filteredActions = currentActions.filter(action => action.id !== actionId);
        this._pendingActions.set(filteredActions);
    }

    /**
     * Adiciona item ao cache
     */
    adicionarCache(key: string, data: any, ttl: number = this.defaultTTL, priority: 'high' | 'medium' | 'low' = 'medium'): void {
        const cacheItem: CacheItem = {
            key,
            data,
            timestamp: Date.now(),
            ttl,
            priority,
        };

        const currentCache = this._cacheItems();
        currentCache.set(key, cacheItem);
        this._cacheItems.set(new Map(currentCache));

        // Limpar cache excedente se necessário
        if (currentCache.size > this.maxCacheSize) {
            this.limparCacheExcedente();
        }

    }

    /**
     * Obtém item do cache
     */
    obterCache<T>(key: string): T | null {
        const currentCache = this._cacheItems();
        const item = currentCache.get(key);

        if (!item) {
            return null;
        }

        // Verificar se o item expirou
        const now = Date.now();
        if (now - item.timestamp > item.ttl) {
            this.removerCache(key);
            return null;
        }

        return item.data as T;
    }

    /**
     * Remove item do cache
     */
    removerCache(key: string): void {
        const currentCache = this._cacheItems();
        currentCache.delete(key);
        this._cacheItems.set(new Map(currentCache));

    }

    /**
     * Limpa cache excedente baseado em prioridade
     */
    private limparCacheExcedente(): void {
        const currentCache = this._cacheItems();
        const items = Array.from(currentCache.entries());

        // Ordenar por prioridade (low primeiro) e timestamp (mais antigo primeiro)
        items.sort((a, b) => {
            const priorityOrder = { low: 0, medium: 1, high: 2 };
            const priorityDiff = priorityOrder[a[1].priority] - priorityOrder[b[1].priority];

            if (priorityDiff !== 0) return priorityDiff;
            return a[1].timestamp - b[1].timestamp;
        });

        // Remover itens excedentes
        const itemsToRemove = items.slice(0, Math.floor(this.maxCacheSize * 0.2)); // Remove 20% dos itens
        itemsToRemove.forEach(([key]) => {
            this.removerCache(key);
        });
    }

    /**
     * Limpa todo o cache
     */
    limparCacheCompleto(): void {
        this._cacheItems.set(new Map());
    }

    /**
     * Normaliza URL adicionando /api se necessário
     */
    private normalizeUrl(url: string): string {
        const baseUrl = getBaseUrl();

        // Se já começa com /api, retornar como está
        if (url.startsWith('/api')) {
            return url;
        }

        // Se já começa com baseUrl, retornar como está
        if (url.startsWith(baseUrl)) {
            return url;
        }

        // Se é URL completa (http://), retornar como está
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }

        // Adicionar /api se URL começa com /
        if (url.startsWith('/')) {
            return `${baseUrl}${url}`;
        }

        // Adicionar /api/ se URL não começa com /
        return `${baseUrl}/${url}`;
    }

    /**
     * Gera ID único para ações
     */
    private gerarIdUnico(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    /**
     * Força sincronização manual
     */
    forcarSincronizacao(): Promise<void> {
        return this.sincronizarAcoesPendentes();
    }

    /**
     * Obtém estatísticas do cache
     */
    obterEstatisticasCache(): {
        totalItems: number;
        highPriority: number;
        mediumPriority: number;
        lowPriority: number;
        expiredItems: number;
    } {
        const currentCache = this._cacheItems();
        const now = Date.now();
        let highPriority = 0,
            mediumPriority = 0,
            lowPriority = 0,
            expiredItems = 0;

        currentCache.forEach(item => {
            if (now - item.timestamp > item.ttl) {
                expiredItems++;
            } else {
                switch (item.priority) {
                    case 'high':
                        highPriority++;
                        break;
                    case 'medium':
                        mediumPriority++;
                        break;
                    case 'low':
                        lowPriority++;
                        break;
                }
            }
        });

        return {
            totalItems: currentCache.size,
            highPriority,
            mediumPriority,
            lowPriority,
            expiredItems,
        };
    }
}
